// lib/useCallGuard.ts — guard an in-progress live call against navigation & tab close (spec §6.5).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface CallGuard {
  /** The pending in-app destination while the confirm dialog is shown (null when idle). */
  pending: string | null;
  /** Leave: end the call, then navigate to the pending destination. */
  confirm: () => void;
  /** Stay: dismiss the prompt and keep the call going. */
  cancel: () => void;
}

/**
 * While `active`, guards an in-progress live call:
 *  - in-app link click → intercepted; surfaces a confirm prompt (`pending`) instead of navigating
 *  - confirm → cancel the call + navigate; cancel → stay
 *  - browser close / refresh → native prompt, and the call is cancelled regardless of the choice
 *  - unmount / route change → cancel the call
 */
export function useCallGuard(active: boolean, endCall: () => void): CallGuard {
  const [pending, setPending] = useState<string | null>(null);
  const navigate = useNavigate();
  const endRef = useRef(endCall);
  endRef.current = endCall;

  useEffect(() => {
    if (!active) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      endRef.current(); // any close cancels the call (spec §6.5)
      e.preventDefault();
      e.returnValue = '';
    };

    const onClickCapture = (e: MouseEvent) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const anchor = (e.target as Element | null)?.closest?.('a');
      const href = anchor?.getAttribute('href');
      // Only guard same-tab, in-app navigations (skip hashes, external schemes, new tabs).
      if (!href || href.startsWith('#') || /^[a-z]+:/i.test(href) || anchor?.target === '_blank') {
        return;
      }
      // Intercept the SPA navigation and ask first — via a proper dialog, not window.confirm.
      e.preventDefault();
      e.stopPropagation();
      setPending(href);
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClickCapture, true);
      endRef.current(); // leaving the page / ending the guard → cancel the call
    };
  }, [active]);

  const confirm = useCallback(() => {
    const href = pending;
    setPending(null);
    endRef.current();
    if (href) navigate(href);
  }, [pending, navigate]);

  const cancel = useCallback(() => setPending(null), []);

  return { pending, confirm, cancel };
}
